import { Glossary } from '@/kontent/content-types'
import { contentTypes } from '@/kontent/project/contentTypes'
import { Mapping } from '@/types'
import { FetchLinksFromAzureResponseData } from '@/types/fetchLinksFromAzure'
import { getUrlFromMapping } from '@/utils'
import { getSyllabusUrlFromMappingBySyllabusCodename } from '@/utils/getSyllabusUrlFromMapping'
import type {
	IContentItemsContainer,
	ILink,
	Responses,
} from '@kontent-ai/delivery-sdk'
import { LinkProps } from 'next/link'
import { Dispatch, ReactNode, SetStateAction } from 'react'
import { useKontentHomeConfig } from '../contexts/KontentHomeConfigProvider'
import { RichTextProps } from '../RichText'
import { RichtextLinkGlossary } from './RichtextLinkGlossary'
import { RichtextLinkGlossaryNew } from './RichtextLinkGlossaryNew'
import { RichtextLinkTeachingAdvice } from './RichtextLinkTeachingAdvice'
import { RichtextLinkWeblinkCommon } from './RichtextLinkWeblinkCommon'

export interface LinkPropsExtended extends LinkProps {
	target?: string
}
export interface RichtextLinkWrapperProps {
	link: ILink
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	currentPath?: string
	currentStage?: RichTextProps['currentStage']
	currentYear?: RichTextProps['currentYear']
	currentSyllabus?: RichTextProps['currentSyllabus']
	children: (_params: LinkPropsExtended) => ReactNode
	rootGlossaryPopoverData?:
		| Responses.IViewContentItemResponse<Glossary>
		| FetchLinksFromAzureResponseData
	setGlossaryPopoverParentData?: Dispatch<
		SetStateAction<
			| Responses.IViewContentItemResponse<Glossary>
			| FetchLinksFromAzureResponseData
		>
	>
}

export const RichtextLinkWrapper = ({
	link,
	linkedItems,
	mappings,
	currentStage,
	currentYear,
	currentSyllabus,
	rootGlossaryPopoverData,
	setGlossaryPopoverParentData,
	children,
}: RichtextLinkWrapperProps) => {
	const { preview } = useKontentHomeConfig()

	if (!link) {
		return (
			<>
				{children({
					href: undefined,
				})}
			</>
		)
	}

	if (link.type === contentTypes.teachingadvice.codename) {
		return (
			<RichtextLinkTeachingAdvice
				link={link}
				currentStage={currentStage}
				currentYear={currentYear}
				currentSyllabus={currentSyllabus}
			>
				{children}
			</RichtextLinkTeachingAdvice>
		)
	}

	if (link.type === 'link_glossary') {
		return (
			<RichtextLinkGlossaryNew
				link={link}
				rootGlossaryPopoverData={
					rootGlossaryPopoverData as FetchLinksFromAzureResponseData
				}
				setGlossaryPopoverParentData={setGlossaryPopoverParentData}
			>
				{children}
			</RichtextLinkGlossaryNew>
		)
	}

	if (link.type === contentTypes.glossary.codename) {
		return (
			<RichtextLinkGlossary
				link={link}
				rootGlossaryPopoverData={
					rootGlossaryPopoverData as Responses.IViewContentItemResponse<Glossary>
				}
				setGlossaryPopoverParentData={setGlossaryPopoverParentData}
			>
				{children}
			</RichtextLinkGlossary>
		)
	}

	if (
		[
			contentTypes.web_link_cc_focusarea.codename,
			contentTypes.web_link_cc_contentgroup.codename,
			contentTypes.web_link_cc_teachingadvice.codename,
			contentTypes.web_link_focusarea.codename,
			contentTypes.web_link_contentgroup.codename,
			contentTypes.web_link_teachingadvice.codename,
			contentTypes.weblinkint.codename,
			contentTypes.weblinkext.codename,
			contentTypes.web_link_syllabus.codename,
			contentTypes.link_resource_file.codename,
			contentTypes.weblinkvideo.codename,
		].includes(link.type)
	) {
		return (
			<RichtextLinkWeblinkCommon link={link} preview={preview}>
				{children}
			</RichtextLinkWeblinkCommon>
		)
	}

	const _linkedItem = link?.codename ? linkedItems[link.codename] : undefined
	if (_linkedItem) {
		const isTypeWebLinkSyllabus =
			link.type === contentTypes.web_link_syllabus.codename
		if (
			link.type === contentTypes.syllabus.codename ||
			isTypeWebLinkSyllabus
		) {
			const syllabusPath = getSyllabusUrlFromMappingBySyllabusCodename(
				mappings,
				link?.codename,
				true,
			)

			return (
				<>
					{children({
						href: syllabusPath,
					})}
				</>
			)
		}

		return (
			<>
				{children({
					href: getUrlFromMapping(mappings, link?.codename),
				})}
			</>
		)
	}

	return (
		<>
			{children({
				href: getUrlFromMapping(mappings, link?.codename),
			})}
		</>
	)
}
